import { useCreatorBulkUpload } from '@round/creatorbase-helpers/api-hooks';
import { Accordion, Empty, lucideIcons, Skeleton } from '@round/creatorbase-ui';
import ClassifiedSection from '../ClassifiedSection/ClassifiedSection';
import BulkUploadSummary from './components/BulkUploadSummary/BulkUploadSummary';
import CreatedItems from './components/CreatedItems/CreatedItems';
import UploadItemsTable from './components/UploadItemsTable/UploadItemsTable';
import { useBulkUploadItemRows } from './useBulkUploadItemRows';
import styles from './BulkUploadResults.module.css';

const { AlertTriangle } = lucideIcons;

const CREATED_SECTION = 'created';
const SKELETON_SECTION_COUNT = 3;

type Props = {
    bulkUploadId: number;
};

/**
 * One upload's results: its summary above the creators it was made of. The two load separately, so
 * a summary that fails or lags does not hold back the list, and the other way round.
 */
const BulkUploadResults = ({ bulkUploadId }: Props) => {
    const { rows, isLoading, isExploreLoading, isImagesLoading, isError, errorMessage } =
        useBulkUploadItemRows(bulkUploadId);

    // The same upload the summary reads, so the request is shared with it. Until it is in, the upload is
    // taken to be still processing, which keeps its creators from being added to a pool before then.
    const { upload } = useCreatorBulkUpload(bulkUploadId);
    const isProcessing = !upload || upload.status === 'PROCESSING';

    return (
        <>
            <BulkUploadSummary bulkUploadId={bulkUploadId} />
            <BulkUploadItems
                rows={rows}
                isLoading={isLoading}
                isExploreLoading={isExploreLoading}
                isImagesLoading={isImagesLoading}
                isError={isError}
                errorMessage={errorMessage}
                isProcessing={isProcessing}
            />
        </>
    );
};

export default BulkUploadResults;

type ItemsProps = ReturnType<typeof useBulkUploadItemRows> & {
    /** Whether the upload is still processing, which holds back adding its creators to a pool. */
    isProcessing: boolean;
};

/**
 * The upload's creators split by how they got on, created first and open, since those are the ones
 * that can be taken further. A section with nothing in it is left out.
 */
const BulkUploadItems = ({
    rows,
    isLoading,
    isExploreLoading,
    isImagesLoading,
    isError,
    errorMessage,
    isProcessing,
}: ItemsProps) => {
    if (isError) {
        return (
            <div className={styles.emptyState}>
                <Empty className={styles.empty}>
                    <Empty.Header>
                        <Empty.Media variant="icon">
                            <AlertTriangle />
                        </Empty.Media>
                        <Empty.Title>Couldn't load this upload's creators</Empty.Title>
                        {errorMessage && <Empty.Description>{errorMessage}</Empty.Description>}
                    </Empty.Header>
                </Empty>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={styles.skeletons}>
                {Array.from({ length: SKELETON_SECTION_COUNT }).map((_, index) => (
                    <Skeleton key={index} className={styles.sectionSkeleton} />
                ))}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className={styles.emptyState}>
                <Empty className={styles.empty}>
                    <Empty.Header>
                        <Empty.Title>No creators to show</Empty.Title>
                        <Empty.Description>This upload has no creators in it.</Empty.Description>
                    </Empty.Header>
                </Empty>
            </div>
        );
    }

    const created = rows.filter((row) => row.status === 'CREATED');
    const inProgress = rows.filter((row) => row.status === 'PENDING' || row.status === 'PROCESSING');
    const failed = rows.filter((row) => row.status === 'FAILED');

    return (
        <Accordion type="multiple" defaultValue={[CREATED_SECTION]} className={styles.sections}>
            {created.length > 0 && (
                <ClassifiedSection value={CREATED_SECTION} title="Created" count={created.length}>
                    <CreatedItems
                        rows={created}
                        isExploreLoading={isExploreLoading}
                        isImagesLoading={isImagesLoading}
                        isProcessing={isProcessing}
                    />
                </ClassifiedSection>
            )}

            {inProgress.length > 0 && (
                <ClassifiedSection value="in-progress" title="In progress" count={inProgress.length}>
                    <UploadItemsTable rows={inProgress} />
                </ClassifiedSection>
            )}

            {failed.length > 0 && (
                <ClassifiedSection value="failed" title="Failed" count={failed.length}>
                    <UploadItemsTable rows={failed} />
                </ClassifiedSection>
            )}
        </Accordion>
    );
};
